import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/utils/api';

// Single source of truth for the "How to Register" step list — used by both
// the desktop hero overlay and the mobile hero panel. Edit here only.
const REGISTRATION_STEPS = [
  { n: 1, title: 'Create your family account', desc: 'Click Register, then enter your email, a username, and a password.', time: '~2 min' },
  { n: 2, title: 'Complete your family profile', desc: 'Tell us about your household, your children, and your interests.', time: '~7 min' },
  { n: 3, title: 'Receive your welcome letter', desc: 'Within minutes, you\u2019ll get a welcome email confirming your profile.', time: '< 10 min' },
  { n: 4, title: 'Pick your delivery window', desc: 'When you complete your family profile, we\u2019ll send you an email invitation to choose a date for delivery.', time: 'When invited' },
  { n: 5, title: 'Enjoy your in-home library', desc: 'A volunteer delivers your bookshelf and starter books to your door.', time: 'Delivery day' },
];

export default function Login() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', password: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);

  const [showPendingApprovalMessage, setShowPendingApprovalMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', loginData);
      const { token, user } = response.data;
      
      // Check if user must change password
      if (user.must_change_password) {
        setTempToken(token);
        setTempUser(user);
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success('Login successful!');
      
      // Check if profile is completed
      if (user.role === 'family' && !user.profile_completed) {
        navigate('/complete-profile');
      } else if (user.role === 'family') {
        navigate('/family-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Check if it's a pending approval error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('pending approval')) {
        toast.error('Your account is pending approval. Please wait for admin approval.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      // Temporarily set token for API call
      localStorage.setItem('token', tempToken);
      
      await api.post('/auth/set-new-password', { new_password: newPassword });
      
      // Update user object to remove must_change_password flag
      const updatedUser = { ...tempUser, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Password updated successfully! Welcome to The Bookshelf Project!');
      setShowPasswordChange(false);
      
      if (tempUser.role === 'family') {
        navigate('/family-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
      localStorage.removeItem('token');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.email) {
      toast.error('Email is required for registration');
      return;
    }
    
    if (!registerData.username || registerData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    if (!registerData.password || registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      // Force role to 'family' for public registration
      await api.post('/auth/register', {
        ...registerData,
        role: 'family'
      });
      
      // Show success message - auto-approved
      setRegisteredEmail(registerData.email);
      setShowPendingApprovalMessage(true);
      toast.success('Account created! You can now log in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 relative overflow-hidden hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1576108700272-8d9f86c51273?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxibGFjayUyMHN0dWRlbnRzJTIwcmVhZGluZyUyMGJvb2tzJTIwbGlicmFyeSUyMGdyb3VwJTIwc21pbGV8ZW58MHx8fHwxNzcxNDMwNTY4fDA&ixlib=rb-4.1.0&q=85"
          alt="Happy child reading book"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/80"></div>

        {/* How to Register — overlay panel */}
        <div className="absolute inset-0 flex items-center justify-center p-10 overflow-y-auto">
          <div className="max-w-lg w-full text-white">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-white/70 font-semibold mb-1">
                Welcome to The Bookshelf Project
              </p>
              <h2 className="text-3xl font-bold leading-tight mb-2">
                How to Register for Your Free Bookshelf
              </h2>
              <p className="text-sm text-white/85">
                Five simple steps · Takes about 10 minutes
              </p>
            </div>

            <ol className="space-y-4" data-testid="registration-steps">
              {REGISTRATION_STEPS.map((step) => (
                <li
                  key={step.n}
                  className="flex gap-4 items-start"
                  data-testid={`step-${step.n}`}
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white text-primary font-bold text-sm flex items-center justify-center shadow-sm">
                    {step.n}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-white leading-tight">
                        {step.title}
                      </h3>
                      <span className="text-xs text-white/60">· {step.time}</span>
                    </div>
                    <p className="text-sm text-white/80 leading-snug mt-0.5">
                      {step.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-6 pt-4 border-t border-white/20 text-xs text-white/70">
              Questions? Email{' '}
              <a
                href="mailto:info@thebookshelfproject.org"
                className="underline hover:text-white"
              >
                info@thebookshelfproject.org
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex items-start lg:items-center justify-center p-6 lg:p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile-only: branded hero panel with all 5 steps shown above the form */}
          <div
            className="lg:hidden -mx-6 -mt-6 mb-6 bg-primary text-white px-6 py-8 rounded-b-2xl shadow-md"
            data-testid="mobile-steps-hero"
          >
            <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold mb-1">
              Welcome to The Bookshelf Project
            </p>
            <h2 className="text-2xl font-bold leading-tight mb-1">
              How to Register for Your Free Bookshelf
            </h2>
            <p className="text-sm text-white/85 mb-5">
              Five simple steps · Takes about 10 minutes
            </p>
            <ol className="space-y-3">
              {REGISTRATION_STEPS.map((step) => (
                <li
                  key={step.n}
                  className="flex gap-3 items-start"
                  data-testid={`mobile-step-${step.n}`}
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white text-primary font-bold text-xs flex items-center justify-center">
                    {step.n}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-white leading-tight">
                        {step.title}
                      </h3>
                      <span className="text-[11px] text-white/60">· {step.time}</span>
                    </div>
                    <p className="text-xs text-white/85 leading-snug mt-0.5">
                      {step.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-5 pt-3 border-t border-white/20 text-[11px] text-white/70">
              Questions? Email{' '}
              <a href="mailto:info@thebookshelfproject.org" className="underline hover:text-white">
                info@thebookshelfproject.org
              </a>
            </div>
          </div>

          <Card className="w-full border-stone-200 shadow-floating" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-4 bg-gradient-to-br from-primary/5 to-accent/10 rounded-2xl border border-primary/10 shadow-sm">
              <img 
                src="https://customer-assets.emergentagent.com/job_literacy-track-2/artifacts/ne4pq461_TBP-New%20Logo%20Design%20conv%201.png"
                alt="The Bookshelf Project"
                className="h-20 w-auto mx-auto"
                data-testid="login-logo"
              />
            </div>
            <CardTitle className="text-3xl">Welcome</CardTitle>
            <CardDescription>Sign in to your account or create a new one</CardDescription>
            <a
              href="https://www.thebookshelfproject.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-primary mt-3 transition-colors"
              data-testid="back-to-main-site-link"
            >
              ← Back to thebookshelfproject.org
            </a>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-username">Username</Label>
                    <Input
                      id="login-username"
                      data-testid="login-username-input"
                      type="text"
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <PasswordInput
                      id="login-password"
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    className="w-full rounded-full"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <div className="text-center pt-2">
                    <Link 
                      to="/admin-login"
                      className="text-sm text-primary hover:underline"
                      data-testid="admin-signin-link"
                    >
                      Admin Sign In
                    </Link>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email Address *</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      value={registerData.email || ''}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="h-11"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-username">Username *</Label>
                    <Input
                      id="register-username"
                      data-testid="register-username-input"
                      type="text"
                      value={registerData.username}
                      onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password *</Label>
                    <PasswordInput
                      id="register-password"
                      data-testid="register-password-input"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="h-11"
                    />
                    <p className="text-xs text-stone-500">Must be at least 6 characters</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    After creating your account, you can log in immediately to complete your family profile. You'll receive a welcome letter once your profile is submitted.
                  </div>
                  <Button
                    type="submit"
                    data-testid="register-submit-button"
                    className="w-full rounded-full"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Create Family Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        </div>
      </div>
      
      {/* Password Change Dialog for Migrated Users */}
      <Dialog open={showPasswordChange} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="password-change-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">Welcome! Please Set Your New Password</DialogTitle>
            <DialogDescription>
              For your security, please create a new password to replace your temporary login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <PasswordInput
                id="new-password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                className="h-11"
              />
              <p className="text-xs text-stone-500">Must be at least 6 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <PasswordInput
                id="confirm-password"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="h-11"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handlePasswordChange}
              disabled={changingPassword || !newPassword || !confirmPassword}
              className="w-full rounded-full"
              data-testid="set-password-button"
            >
              {changingPassword ? 'Updating...' : 'Set New Password & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Approval Dialog */}
      <Dialog open={showPendingApprovalMessage} onOpenChange={setShowPendingApprovalMessage}>
        <DialogContent className="sm:max-w-md" data-testid="registration-success-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">Account Created Successfully!</DialogTitle>
            <DialogDescription>
              You can now log in and complete your family profile.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 text-sm">
                Your account has been created with:
              </p>
              <p className="font-medium text-blue-900 mt-1">{registeredEmail}</p>
            </div>
            <div className="space-y-3 text-sm text-stone-600">
              <p><strong>What happens next:</strong></p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Log in with the username and password you just created</li>
                <li>Complete your family profile</li>
                <li>You'll receive a welcome letter with next steps for scheduling your bookshelf delivery</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowPendingApprovalMessage(false)}
              className="w-full rounded-full"
              data-testid="registration-success-ok-button"
            >
              Log In Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}